import React from 'react';
import { ChevronDown, ChevronRight, Users, BookOpen, Briefcase, Sun, Moon, Globe2 } from 'lucide-react';
import { ServiceLink } from './types';
import PaperPlanes from './PaperPlanes';
import { useTheme } from '../../contexts/ThemeContext';

interface MobileNavProps {
  isOpen: boolean;
  serviceLinks: ServiceLink[];
  isServicesOpen: boolean;
  language: 'en' | 'de';
  onServicesToggle: () => void;
  onLanguageToggle: () => void;
  onScrollToSection: (sectionId: string) => void;
  onNavigate: (path: string) => void;
}

const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  serviceLinks,
  isServicesOpen,
  language,
  onServicesToggle,
  onLanguageToggle,
  onScrollToSection,
  onNavigate
}) => {
  const { isDarkMode, toggleTheme } = useTheme();

  const handleNavigation = (path: string) => {
    onNavigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div 
      className={`md:hidden fixed inset-0 bg-secondary-black/98 backdrop-blur-xl 
                 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
                 z-40`}
      style={{ top: '80px' }}
    >
      <div className="relative h-full">
        <PaperPlanes />
        <div className="container mx-auto px-4 py-6 h-[calc(100vh-80px)] overflow-y-auto relative z-10">
          <nav className="flex flex-col space-y-6">
            {/* Services Menu */}
            <div className="space-y-4">
              <button
                onClick={onServicesToggle}
                className="flex items-center justify-between w-full text-left text-primary-mint 
                         font-medium py-2 active:scale-98 transform transition-transform"
              >
                <span>Services</span>
                <ChevronDown 
                  className={`w-5 h-5 transition-transform duration-300 
                           ${isServicesOpen ? 'rotate-180' : ''}`} 
                />
              </button>

              <div className={`space-y-4 transition-all duration-300 overflow-hidden
                            ${isServicesOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {serviceLinks.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigation(service.path)}
                    className="block w-full text-left pl-4 py-3 border-l-2 border-primary-mint/20 
                             hover:border-primary-mint hover:text-primary-mint 
                             transition-colors active:bg-primary-mint/5
                             backdrop-blur-md bg-secondary-black/30 rounded-r-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-primary-mint">{service.title}</div>
                        <div className="text-sm text-secondary-cream/60">{service.description}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-primary-mint/40" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation Links */}
            <button 
              onClick={() => handleNavigation('/about')}
              className="flex items-center space-x-3 text-primary-mint py-3 active:text-primary-green
                       backdrop-blur-md bg-secondary-black/30 px-4 rounded-lg w-full text-left"
            >
              <Users className="w-5 h-5" />
              <span>About Us</span>
            </button>

            <button 
              onClick={() => handleNavigation('/blog')}
              className="flex items-center space-x-3 text-primary-mint py-3 active:text-primary-green
                       backdrop-blur-md bg-secondary-black/30 px-4 rounded-lg w-full text-left"
            >
              <BookOpen className="w-5 h-5" />
              <span>Blog</span>
            </button>

            <button 
              onClick={() => handleNavigation('/career')}
              className="flex items-center space-x-3 text-primary-mint py-3 active:text-primary-green
                       backdrop-blur-md bg-secondary-black/30 px-4 rounded-lg w-full text-left"
            >
              <Briefcase className="w-5 h-5" />
              <span>Careers</span>
            </button>

            <button 
              onClick={() => onScrollToSection('contact')}
              className="bg-primary-mint/10 border border-primary-mint/30 px-6 py-4 rounded-full 
                       text-primary-mint hover:bg-primary-mint/20 transition-all text-center text-lg
                       active:scale-98 transform backdrop-blur-md"
            >
              Contact
            </button>

            <div className="flex items-center justify-between pt-4 border-t border-primary-mint/20">
              <button
                onClick={toggleTheme}
                className="flex items-center space-x-3 text-primary-mint py-3 active:text-primary-green
                         backdrop-blur-md bg-secondary-black/30 px-4 rounded-lg"
              >
                {isDarkMode ? 
                  <><Sun className="w-5 h-5" /><span>Light Mode</span></> : 
                  <><Moon className="w-5 h-5" /><span>Dark Mode</span></>
                }
              </button>

              <button
                onClick={onLanguageToggle}
                className="flex items-center space-x-3 text-primary-mint py-3 active:text-primary-green
                         backdrop-blur-md bg-secondary-black/30 px-4 rounded-lg"
              >
                <Globe2 className="w-5 h-5" />
                <span>{language === 'en' ? 'Deutsch' : 'English'}</span>
              </button>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default MobileNav;